/**
 * ForgotPassword — calm reset-request screen.
 *
 * Step 1 of the two-step password reset flow. Parent submits their email →
 * backend POST /api/auth/password-reset/request → screen flips to a calm
 * "If that email matches…" confirmation. We never echo whether the account
 * exists (account-enumeration safe).
 *
 * Paired with ResetPassword.jsx (step 2) which consumes the emailed token.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import MetaTags from "../components/MetaTags";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast(t("auth.forgot.rate_limit_calm"));
      } else {
        toast(
          typeof detail === "string"
            ? detail
            : t("auth.forgot.generic_error")
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF9F1]">
      <MetaTags
        title="Reset your Alouvya password"
        description="Calm, secure password reset. We'll email a one-time link that expires in an hour."
      />

      <div className="px-6 sm:px-12 py-8 flex items-center justify-between">
        <Link
          to="/"
          data-testid="forgot-brand"
          className="flex items-center"
        >
          <Brand size="sm" dataTestId="brand-forgot" />
        </Link>
        <Link
          to="/signin"
          data-testid="forgot-back-to-signin"
          className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F3A2A]"
        >
          <ArrowLeft size={12} strokeWidth={1.6} />
          {t("auth.forgot.back_to_signin")}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md fade-up">
          {sent ? (
            <div data-testid="forgot-sent-state">
              <span className="eyebrow">{t("auth.forgot.sent_eyebrow")}</span>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-[#1A1B2E]">
                {t("auth.forgot.sent_title_line1")}
                <br />
                <em className="italic text-[#8A9984]">{t("auth.forgot.sent_title_italic")}</em>
              </h1>
              <p className="mt-7 text-[15px] leading-relaxed text-[#6B7280]">
                {t("auth.forgot.sent_body_prefix")}{" "}
                <span className="text-[#1A1B2E] font-medium">{email}</span>{" "}
                {t("auth.forgot.sent_body_suffix")}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/signin"
                  data-testid="forgot-return-signin"
                  className="btn-primary"
                >
                  {t("auth.forgot.sent_return_signin")}
                </Link>
                <button
                  type="button"
                  data-testid="forgot-send-another"
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                  }}
                  className="btn-ghost"
                >
                  {t("auth.forgot.sent_send_another")}
                </button>
              </div>
              <p className="mt-8 text-xs text-[#9CA3AF]">
                {t("auth.forgot.sent_didnt_receive")}
              </p>
            </div>
          ) : (
            <>
              <span className="eyebrow">{t("auth.forgot.title_line1")}</span>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-[#1A1B2E]">
                {t("auth.forgot.title_line1")}
                <br />
                <em className="italic text-[#8A9984]">{t("auth.forgot.title_italic")}</em>
              </h1>
              <p className="mt-6 text-[15px] leading-relaxed text-[#6B7280]">
                {t("auth.forgot.body")}
              </p>

              <form onSubmit={onSubmit} className="mt-10 space-y-8">
                <div>
                  <label htmlFor="forgot-email" className="eyebrow">{t("auth.email")}</label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="orbit-input mt-3"
                    placeholder={t("auth.email_placeholder")}
                    data-testid="forgot-email"
                  />
                </div>
                <div className="pt-2 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={busy || !email.trim()}
                    data-testid="forgot-submit"
                    className="btn-primary"
                  >
                    {busy ? t("auth.forgot.submit_pending") : t("auth.forgot.submit")}
                  </button>
                  <Link to="/signin" className="btn-ghost" data-testid="forgot-cancel">
                    {t("auth.forgot.cancel")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="px-6 sm:px-12 py-8 text-xs text-[#9CA3AF] tracking-wide text-center">
        Calm, clarity, and trust · Alouvya
      </div>
    </div>
  );
}
